<template>
  <div class="promo-view">
    <transition name="promo-view-fade" appear>
      <LoadingIndicator
        class="promo-view__loader"
        :isVisible="isSubmitting && hasPromoOrGiftCard"
        :type="LOADING_INDICATOR_TYPES.spinner"
      />
    </transition>
    <span class="promo-view__code">
      <template v-if="isGiftCard">
        <span class="promo-view__promo-type">
          <IconGiftCard class="promo-view__gift-card-icon" v-data-test-id="'gift-card-icon'" />
          {{ promotionType }}
        </span>
      </template>
      <template v-else>
        <span
          v-if="variant !== VARIANTS.tall"
          class="promo-view__promo-type promo-view__promo-type--code"
        >
          {{ promotionType }}:
        </span>
        <span class="promo-view__code-name" v-data-test-id="'promo-code-name'">{{ codeName }}</span>
        <a
          class="promo-view__remove-link"
          v-data-test-id="'promo-code-remove'"
          @click="$emit('click:remove-code')"
        >
          {{ $t('remove') }}
        </a>
      </template>
    </span>
    <span class="promo-view__discount">{{ discount }}</span>
  </div>
</template>

<script>
import { includesValue } from '@/helpers/object'
import IconGiftCard from '@/components/_icons/IconGiftCard.svg'
import LoadingIndicator, {
  LOADING_INDICATOR_TYPES,
} from '@/components/_shared/LoadingIndicator.vue'
import { VARIANTS } from '@/components/_shared/Form/form-helper'

export default {
  name: 'PromoView',
  components: {
    IconGiftCard,
    LoadingIndicator,
  },

  props: {
    variant: {
      type: String,
      default: VARIANTS.default,
      validator(value) {
        return includesValue(VARIANTS, value)
      },
    },
    hasPromoOrGiftCard: {
      type: Boolean,
      default: false,
    },
    discount: {
      type: String,
      default: '',
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    promotionAdjustment: {
      type: Object,
      default: () => {
        return {}
      },
    },
    isGiftCard: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      LOADING_INDICATOR_TYPES,
      VARIANTS,
    }
  },

  computed: {
    codeName() {
      return this.promotionAdjustment?.code
    },

    promotionType() {
      return this.isGiftCard ? this.$t('gift_card') : this.$t('promotion')
    },
  },
}
</script>

<style lang="scss" scoped>
.promo-view {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  min-height: 56px;
  margin-top: 16px;
  padding: 12px 16px;
  font-size: $font-size-small;
  background: $color-grey-light-4;

  &__loader {
    position: absolute;
    top: 8px;
    left: 0;
    width: 100%;
    height: 100%;
    background: $color-white;
    opacity: 0.7;
    display: flex;
    justify-content: center;
  }

  &__promo-type {
    display: flex;
    align-items: center;

    &--code {
      display: none;

      @media ($mq-large) {
        display: block;
      }
    }
  }

  &__gift-card-icon {
    height: 30px;
    width: auto;
    margin-right: 18px;
  }

  &__code {
    display: flex;
    flex: 1;
    min-width: 0;
    color: $color-grey-dark-3;
  }

  &__code-name {
    margin: 0 16px 0 0;
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media ($mq-large) {
      margin: 0 16px 0 4px;
    }

    .promo-code--tall & {
      margin: 0 16px 0 0;
      font-weight: normal;
    }
  }

  &__discount {
    color: $color-grey-dark-3;
  }

  &__remove-link {
    margin-right: 16px;
    text-decoration: underline;
    color: $color-grey-mid-2;
    cursor: pointer;
  }
}

.promo-view-fade-enter-active,
.promo-view-fade-leave-active {
  transition: opacity 0.18s ease-in-out;
}
.promo-view-fade-enter,
.promo-view-fade-leave-to {
  opacity: 0;
}
</style>

<i18n src="@/locales/promo_view.json">
</i18n>
